import type { DailyMeasure } from './math/series'
import type { DashboardDomain } from './types'

export type DashboardTemplate = 'publisher' | 'ecommerce'

type HeadlineTileFields = {
    label: string
    /** The registry measure that entitles and labels the tile. */
    measure: string
    entity: 'notifications' | 'prompts' | 'subscribers'
}

/**
 * A tile is one of three shapes, and carries what that shape needs to be computed: a flow adds up a daily measure
 * across the range, a ratio is recomputed from a summed numerator and denominator, and a level is the subscriber
 * count on the range's last day.
 */
export type HeadlineTileSpec =
    | (HeadlineTileFields & {
          kind: 'flow'
          key: 'clicks' | 'new_subscribers' | 'sends' | 'purchases' | 'revenue'
          dailyMeasure: DailyMeasure
          unit: 'count' | 'money'
      })
    | (HeadlineTileFields & {
          kind: 'ratio'
          key: 'ctr' | 'revenue_per_send'
          numerator: DailyMeasure
          denominator: DailyMeasure
          denominatorNoun: string
          unit: 'rate' | 'money'
      })
    | (HeadlineTileFields & { kind: 'level'; key: 'total_subscribers'; unit: 'count' })

export const TEMPLATE_SLOTS: Record<
    DashboardTemplate,
    {
        headlineTiles: HeadlineTileSpec[]
        domainsColumns: string[]
        defaultSort: string
        contextRate: 'ctr' | 'purchase_rate'
        rankings: {
            primary: 'clicks' | 'purchase_amount'
            secondary: string
            secondaryFloor: { measure: string; minimum: number }
        }
        /** The measures the daily series read, per request. Only those the organization's definitions offer are asked for. */
        seriesMeasures: { engagement: string[]; sends: string[]; acquisition: string[] }
        /** The measures the notification ranking's columns read, beside the ones it ranks and floors by. */
        rankingMeasures: string[]
    }
> = {
    publisher: {
        headlineTiles: [
            {
                kind: 'flow',
                key: 'clicks',
                label: 'Clicks',
                measure: 'clicks',
                entity: 'notifications',
                dailyMeasure: 'clicks',
                unit: 'count',
            },
            {
                kind: 'ratio',
                key: 'ctr',
                label: 'CTR',
                measure: 'ctr_decimal',
                entity: 'notifications',
                numerator: 'clicks',
                denominator: 'impressions',
                denominatorNoun: 'impressions',
                unit: 'rate',
            },
            {
                kind: 'flow',
                key: 'new_subscribers',
                label: 'New Subscribers',
                measure: 'subscriptions',
                entity: 'prompts',
                dailyMeasure: 'subscriptions',
                unit: 'count',
            },
            {
                kind: 'flow',
                key: 'sends',
                label: 'Sends',
                measure: 'notifications_sent',
                entity: 'notifications',
                dailyMeasure: 'sends',
                unit: 'count',
            },
            {
                kind: 'level',
                key: 'total_subscribers',
                label: 'Total Subscribers',
                measure: 'size',
                entity: 'subscribers',
                unit: 'count',
            },
        ],
        domainsColumns: ['subscribers', 'new_subscribers', 'sends', 'clicks', 'ctr', 'context'],
        defaultSort: 'clicks',
        contextRate: 'ctr',
        rankings: {
            primary: 'clicks',
            secondary: 'ctr_decimal',
            secondaryFloor: { measure: 'impressions', minimum: 10_000 },
        },
        seriesMeasures: {
            engagement: ['impressions', 'clicks'],
            sends: ['notifications_sent'],
            acquisition: ['permissions_shown', 'subscriptions'],
        },
        rankingMeasures: ['impressions', 'clicks', 'ctr_decimal'],
    },
    ecommerce: {
        headlineTiles: [
            {
                kind: 'flow',
                key: 'revenue',
                label: 'Revenue',
                measure: 'purchase_amount',
                entity: 'notifications',
                dailyMeasure: 'purchaseAmount',
                unit: 'money',
            },
            {
                kind: 'flow',
                key: 'purchases',
                label: 'Purchases',
                measure: 'purchases',
                entity: 'notifications',
                dailyMeasure: 'purchases',
                unit: 'count',
            },
            {
                kind: 'flow',
                key: 'clicks',
                label: 'Clicks',
                measure: 'clicks',
                entity: 'notifications',
                dailyMeasure: 'clicks',
                unit: 'count',
            },
            {
                kind: 'flow',
                key: 'new_subscribers',
                label: 'New Subscribers',
                measure: 'subscriptions',
                entity: 'prompts',
                dailyMeasure: 'subscriptions',
                unit: 'count',
            },
            {
                kind: 'ratio',
                key: 'revenue_per_send',
                label: 'Revenue per Send',
                measure: 'revenue_per_send',
                entity: 'notifications',
                numerator: 'purchaseAmount',
                denominator: 'sends',
                denominatorNoun: 'sends',
                unit: 'money',
            },
        ],
        domainsColumns: ['revenue', 'purchases', 'clicks', 'revenue_per_send', 'new_subscribers', 'context'],
        defaultSort: 'revenue',
        contextRate: 'purchase_rate',
        rankings: {
            primary: 'purchase_amount',
            secondary: 'purchase_rate_decimal',
            secondaryFloor: { measure: 'clicks', minimum: 1_000 },
        },
        seriesMeasures: {
            engagement: ['impressions', 'clicks', 'purchases', 'purchase_amount'],
            sends: ['notifications_sent'],
            acquisition: ['permissions_shown', 'subscriptions'],
        },
        rankingMeasures: [
            'impressions',
            'clicks',
            'ctr_decimal',
            'purchases',
            'purchase_amount',
            'purchase_rate_decimal',
        ],
    },
}

/**
 * E-commerce when at least one of the organization's domains has e-commerce enabled, otherwise publisher. It reads
 * the organization's whole domain list, never the domain filter or the range, so filtering does not change it.
 */
export const templateFor = (domains: Pick<DashboardDomain, 'ecommerceEnabled'>[]): DashboardTemplate =>
    domains.some((domain) => domain.ecommerceEnabled) ? 'ecommerce' : 'publisher'

/**
 * The measure the template's primary ranking orders by. Revenue in different currencies cannot be ranked
 * together, so an e-commerce organization whose selected domains mix currencies ranks by purchases instead.
 */
export const primaryRankingMeasureFor = (
    template: DashboardTemplate,
    mixedCurrency: boolean,
): 'clicks' | 'purchase_amount' | 'purchases' =>
    template === 'ecommerce' && mixedCurrency ? 'purchases' : TEMPLATE_SLOTS[template].rankings.primary
